/**
 * Position (trade-forecasting box) renderer, extracted from Chart.tsx. The
 * most interactive drawing: TP/SL zones, three price lines, a body hit-rect
 * that routes drag by proximity, and corner/edge resize handles. It writes
 * the shared dragRef and reads the live chart/series/container refs at event
 * time, so the context carries the refs themselves. Body is verbatim; only
 * closed-over references became ctx.*.
 */
import type { MouseEvent, MutableRefObject, RefObject, ReactNode } from "react";
import type { IChartApi } from "lightweight-charts";
import {
  type Position,
  type SelectedDrawing,
  setSelectedDrawing,
} from "@/state/slices/uiSlice";
import type { AppDispatch } from "@/state/store";
import type { AnySeries } from "./chartData";
import type { DragTarget, MenuItem } from "./types";
import styles from "./Chart.module.css";

export interface PositionRenderCtx {
  seriesRef: MutableRefObject<AnySeries | null>;
  chartRef: MutableRefObject<IChartApi | null>;
  containerRef: RefObject<HTMLDivElement>;
  dragRef: MutableRefObject<DragTarget | null>;
  timeToX: (time: number) => number | null;
  selectedDrawing: SelectedDrawing | null;
  startDrag: (
    target: Exclude<DragTarget, { type: "trading" } | { type: "draft" }>,
  ) => (e: MouseEvent) => void;
  showMenu: (items: MenuItem[]) => (e: MouseEvent) => void;
  dispatch: AppDispatch;
  tabId: string;
  setIsDragging: (v: boolean) => void;
  positionMenuItems: (posId: string) => MenuItem[];
  digits: number;
}

export function renderPosition(
  pos: Position,
  isPreview: boolean,
  ctx: PositionRenderCtx,
): ReactNode {
  if (pos.visible === false) return null;
  const series = ctx.seriesRef.current;
  if (!series) return null;

  const startX = ctx.timeToX(pos.startTime);
  const endX = ctx.timeToX(pos.endTime);
  const entryY = series.priceToCoordinate(pos.entryPrice);
  const slY = series.priceToCoordinate(pos.slPrice);
  const tpY = series.priceToCoordinate(pos.tpPrice);
  if (
    startX == null ||
    endX == null ||
    entryY == null ||
    slY == null ||
    tpY == null
  )
    return null;

  const left = Math.min(startX, endX);
  const right = Math.max(startX, endX);
  const width = Math.max(2, right - left);
  const isSelected =
    !isPreview &&
    ctx.selectedDrawing?.kind === "position" &&
    ctx.selectedDrawing.id === pos.id;

  // TP rect: from entry up to TP for long, down to TP for short.
  const tpTop = Math.min(entryY, tpY);
  const tpHeight = Math.abs(tpY - entryY);
  const slTop = Math.min(entryY, slY);
  const slHeight = Math.abs(slY - entryY);

  const reward = Math.abs(pos.tpPrice - pos.entryPrice);
  const risk = Math.abs(pos.entryPrice - pos.slPrice);
  const rr = risk > 0 ? reward / risk : 0;
  const pctMove = pos.entryPrice
    ? ((pos.tpPrice - pos.entryPrice) / pos.entryPrice) * 100
    : 0;
  const slPct = pos.entryPrice
    ? ((pos.entryPrice - pos.slPrice) / pos.entryPrice) * 100
    : 0;
  const pipDist = (a: number, b: number) =>
    // Display as raw price units at the symbol's own precision; metals/FX
    // users read this as $-distance per unit, finer granularity than
    // "pips" which mean different things across instruments.
    Math.abs(a - b).toFixed(ctx.digits);

  const tpColor = "#26A69A";
  const slColor = "#F23645";
  const entryColor = isSelected ? "#3b82f6" : "#FFC107";

  // Label positions: pin to the right edge of the box so they sit on
  // top of the rectangles rather than overflowing into the candles.
  const labelX = right - 8;

  return (
    <g
      key={isPreview ? "preview-position" : pos.id}
      opacity={isPreview ? 0.55 : 1}
    >
      {/* TP zone */}
      <rect
        x={left}
        y={tpTop}
        width={width}
        height={tpHeight}
        fill={tpColor}
        fillOpacity={0.18}
        stroke={tpColor}
        strokeOpacity={isSelected ? 0.9 : 0.5}
        strokeWidth={isSelected ? 1.5 : 1}
      />
      {/* SL zone */}
      <rect
        x={left}
        y={slTop}
        width={width}
        height={slHeight}
        fill={slColor}
        fillOpacity={0.18}
        stroke={slColor}
        strokeOpacity={isSelected ? 0.9 : 0.5}
        strokeWidth={isSelected ? 1.5 : 1}
      />

      {/* Visible price lines — non-interactive (the body rect below
          owns the mousedown and routes by proximity). */}
      <line
        x1={left}
        x2={right}
        y1={tpY}
        y2={tpY}
        stroke={tpColor}
        strokeWidth={1.5}
        pointerEvents="none"
      />
      <line
        x1={left}
        x2={right}
        y1={entryY}
        y2={entryY}
        stroke={entryColor}
        strokeWidth={1.5}
        strokeDasharray="4 3"
        pointerEvents="none"
      />
      <line
        x1={left}
        x2={right}
        y1={slY}
        y2={slY}
        stroke={slColor}
        strokeWidth={1.5}
        pointerEvents="none"
      />

      {/* Body rect — the ONLY interior hit-target. Its mousedown
          decides what to do based on proximity to the price lines
          and time edges:
            - within 4 px of TP / Entry / SL line → single-axis line resize
            - within 4 px of left / right edge   → time-axis edge resize
            - anywhere else                      → body translation
          This gives body drag the dominant area; resize requires a
          precise click near a line / edge. Corner circles (drawn
          after this rect) still catch their own diagonal-drag clicks.
          Skipped in preview. */}
      {!isPreview && (
        <rect
          x={left}
          y={Math.min(tpY, slY)}
          width={width}
          height={Math.abs(slY - tpY)}
          fill="transparent"
          className={styles.posBodyHit}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            e.preventDefault();
            const containerEl = ctx.containerRef.current;
            const chartApi = ctx.chartRef.current;
            const seriesApi = ctx.seriesRef.current;
            if (!containerEl || !chartApi || !seriesApi) return;
            const r = containerEl.getBoundingClientRect();
            const xCoord = e.clientX - r.left;
            const yCoord = e.clientY - r.top;
            ctx.dispatch(
              setSelectedDrawing({
                tabId: ctx.tabId,
                selected: { kind: "position", id: pos.id },
              }),
            );
            // Suppress chart pan for the entire drag — otherwise the
            // chart pans simultaneously and the cursor's time/price
            // shifts mid-drag, causing the box to jump/invert.
            ctx.setIsDragging(true);

            const TH = 4;
            // Proximity check — closest hit within 4 px wins. Order
            // matters only if two lines/edges are within 4 px of the
            // click; in practice they won't be.
            if (Math.abs(yCoord - tpY) <= TH) {
              ctx.dragRef.current = { type: "position", id: pos.id, anchor: "tp" };
              return;
            }
            if (Math.abs(yCoord - entryY) <= TH) {
              ctx.dragRef.current = {
                type: "position",
                id: pos.id,
                anchor: "entry",
              };
              return;
            }
            if (Math.abs(yCoord - slY) <= TH) {
              ctx.dragRef.current = { type: "position", id: pos.id, anchor: "sl" };
              return;
            }
            if (Math.abs(xCoord - left) <= TH) {
              ctx.dragRef.current = {
                type: "position",
                id: pos.id,
                anchor: "left",
              };
              return;
            }
            if (Math.abs(xCoord - right) <= TH) {
              ctx.dragRef.current = {
                type: "position",
                id: pos.id,
                anchor: "right",
              };
              return;
            }

            // Body translation — snapshot initial state + cursor anchor so each
            // mousemove can apply a clean delta. The cursor's time is captured as
            // a LOGICAL (bar) position so the move translates in bar space and the
            // box doesn't distort across chart gaps (weekends). See bodyDrag.ts.
            const anchorLogical = chartApi
              .timeScale()
              .coordinateToLogical(xCoord);
            const anchorPrice = seriesApi.coordinateToPrice(yCoord);
            if (anchorLogical == null || anchorPrice == null) return;
            ctx.dragRef.current = {
              type: "position",
              id: pos.id,
              anchor: "body",
              initial: {
                startTime: pos.startTime,
                endTime: pos.endTime,
                entryPrice: pos.entryPrice,
                slPrice: pos.slPrice,
                tpPrice: pos.tpPrice,
                anchorLogical: anchorLogical as number,
                anchorPrice,
              },
            };
          }}
          onContextMenu={ctx.showMenu(ctx.positionMenuItems(pos.id))}
        />
      )}

      {/* Price + percentage labels per line. Anchored right so they
          sit inside the box. */}
      <text
        x={labelX}
        y={tpY - 4}
        textAnchor="end"
        fill={tpColor}
        fontSize={11}
      >
        TP {pos.tpPrice.toFixed(ctx.digits)} · {pctMove >= 0 ? "+" : ""}
        {pctMove.toFixed(2)}% · {pipDist(pos.entryPrice, pos.tpPrice)}
      </text>
      <text
        x={labelX}
        y={entryY - 4}
        textAnchor="end"
        fill={entryColor}
        fontSize={11}
      >
        Entry {pos.entryPrice.toFixed(ctx.digits)}
      </text>
      <text
        x={labelX}
        y={slY + 12}
        textAnchor="end"
        fill={slColor}
        fontSize={11}
      >
        SL {pos.slPrice.toFixed(ctx.digits)} · −{Math.abs(slPct).toFixed(2)}% ·{" "}
        {pipDist(pos.entryPrice, pos.slPrice)}
      </text>

      {/* R:R badge anchored to the LEFT edge of the box — the
          entry/TP/SL labels are on the right, so left-aligning the
          badge keeps both sides readable even on narrow boxes. */}
      <text
        x={left + 6}
        y={entryY - 4}
        textAnchor="start"
        fill="#e5e7eb"
        fontSize={11}
        fontWeight={600}
      >
        {pos.type === "long" ? "LONG" : "SHORT"} · R:R {rr.toFixed(2)}
      </text>

      {/* Corner handles — skipped in preview mode (the position
          isn't committed yet).
          • TP corners (left + right) drag the TP price AND the
            corresponding time edge — diagonal resize.
          • SL corners (left + right) drag SL price + time edge.
          • The middle entry handle stays vertical-only since the
            entry line spans the full width and doesn't have a
            natural "corner". */}
      {!isPreview && (
        <>
          {/* Top-left and top-right TP corners */}
          <circle
            cx={left}
            cy={tpY}
            r={isSelected ? 5 : 4}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={tpColor}
            strokeWidth={1.5}
            className={styles.fibHandle}
            onMouseDown={ctx.startDrag({
              type: "position",
              id: pos.id,
              anchor: "tp-left",
            })}
            onContextMenu={ctx.showMenu(ctx.positionMenuItems(pos.id))}
          />
          <circle
            cx={right}
            cy={tpY}
            r={isSelected ? 5 : 4}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={tpColor}
            strokeWidth={1.5}
            className={styles.fibHandle}
            onMouseDown={ctx.startDrag({
              type: "position",
              id: pos.id,
              anchor: "tp-right",
            })}
            onContextMenu={ctx.showMenu(ctx.positionMenuItems(pos.id))}
          />
          {/* Entry handle (right edge, vertical only) */}
          <circle
            cx={right}
            cy={entryY}
            r={isSelected ? 5 : 4}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={entryColor}
            strokeWidth={1.5}
            className={styles.fibHandle}
            onMouseDown={ctx.startDrag({
              type: "position",
              id: pos.id,
              anchor: "entry",
            })}
            onContextMenu={ctx.showMenu(ctx.positionMenuItems(pos.id))}
          />
          {/* Bottom-left and bottom-right SL corners */}
          <circle
            cx={left}
            cy={slY}
            r={isSelected ? 5 : 4}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={slColor}
            strokeWidth={1.5}
            className={styles.fibHandle}
            onMouseDown={ctx.startDrag({
              type: "position",
              id: pos.id,
              anchor: "sl-left",
            })}
            onContextMenu={ctx.showMenu(ctx.positionMenuItems(pos.id))}
          />
          <circle
            cx={right}
            cy={slY}
            r={isSelected ? 5 : 4}
            fill={isSelected ? "#3b82f6" : "#161922"}
            stroke={slColor}
            strokeWidth={1.5}
            className={styles.fibHandle}
            onMouseDown={ctx.startDrag({
              type: "position",
              id: pos.id,
              anchor: "sl-right",
            })}
            onContextMenu={ctx.showMenu(ctx.positionMenuItems(pos.id))}
          />
        </>
      )}
    </g>
  );
}
